import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';

import {
  baoCaoGetChiTietNhiemVuKhacAction,
  baoCaoClearChiTietNhiemVuKhac,
} from '@app/store/reducers/baocao';

import { URI_INTERNAL } from '@app/utils/pathLocations';
import { objectShowNumber, rangeDateReport, messageToast } from '@app/utils/define';

const getErrMsg = (error) => {
  if (!error) return '';
  let msg =
    (error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error && error.title) ||
    '';

  if (!msg && error && error.errors) {
    msg = Object.values(error.errors).flat().join('; ');
  }
  return msg;
};

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const RANGE_CUSTOM = 'Custom Range';
const RANGE_OPTIONS = [
  'Hôm nay',
  'Hôm qua',
  '7 ngày trước',
  '30 ngày trước',
  'Tháng này',
  'Tháng trước',
  '3 tháng trước',
  '6 tháng trước',
  'Năm',
  RANGE_CUSTOM,
];

const toYMD = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtDDMMYYYY = (ymd) => {
  if (!ymd) return '';
  if (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  }
  const dt = new Date(ymd);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('en-GB');
};

const getRangeFromTo = (label) => {
  if (!label || label === RANGE_CUSTOM) return { from: '', to: '' };
  const pair = rangeDateReport?.[label];
  if (!Array.isArray(pair) || pair.length !== 2) return { from: '', to: '' };
  return { from: toYMD(pair[0]), to: toYMD(pair[1]) };
};

const findPresetLabelByFromTo = (from, to) => {
  const f = String(from || '').trim();
  const t = String(to || '').trim();
  if (!f && !t) return '';

  for (let i = 0; i < RANGE_OPTIONS.length; i++) {
    const label = RANGE_OPTIONS[i];
    if (label === RANGE_CUSTOM) continue;

    const { from: pf, to: pt } = getRangeFromTo(label);
    if (pf === f && pt === t) return label;
  }
  return RANGE_CUSTOM;
};

const getNhiemVuKhacReturnStateFromSearch = (search = '') => {
  const sp = new URLSearchParams(search || '');

  return {
    ignoreCommon: sp.get('nvkIgnoreCommon') === '1',
    rangeLabel: sp.get('nvkRangeLabel') || '',
    from: sp.get('nvkFrom') || '',
    to: sp.get('nvkTo') || '',
  };
};

const BaoCaoChiTietNhiemVuKhac = ({
  From = '',
  To = '',
  commonFrom = '',
  commonTo = '',
  commonRangeLabel = '',
  commonVersion = 0,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    chiTietNhiemVuKhac,
    loadingChiTietNhiemVuKhac,
    errorChiTietNhiemVuKhac,
  } = useSelector((s) => s.baocao || {});

  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [rangeLabel, setRangeLabel] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [appliedTo, setAppliedTo] = useState('');

  const [ignoreCommon, setIgnoreCommon] = useState(false);

  useEffect(() => {
    setIgnoreCommon(false);
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  }, [commonVersion]);

  const hasLocalApplied = !!(appliedFrom || appliedTo);

  const effectiveFrom = useMemo(() => {
    if (hasLocalApplied) return String(appliedFrom || '').trim();
    if (ignoreCommon) return '';
    return String(commonFrom || '').trim();
  }, [hasLocalApplied, appliedFrom, ignoreCommon, commonFrom]);

  const effectiveTo = useMemo(() => {
    if (hasLocalApplied) return String(appliedTo || '').trim();
    if (ignoreCommon) return '';
    return String(commonTo || '').trim();
  }, [hasLocalApplied, appliedTo, ignoreCommon, commonTo]);

  const computedCommonLabel = useMemo(() => {
    const c = String(commonRangeLabel || '').trim();
    if (c) return c;
    const inferred = findPresetLabelByFromTo(commonFrom, commonTo);
    return inferred || '';
  }, [commonRangeLabel, commonFrom, commonTo]);

  const displayRangeLabel = useMemo(() => {
    if (rangeLabel) return rangeLabel;
    if (ignoreCommon) return '';
    return computedCommonLabel || '';
  }, [rangeLabel, ignoreCommon, computedCommonLabel]);

  useEffect(() => {
    const initFrom = String(From || '').trim();
    const initTo = String(To || '').trim();
    if (!initFrom && !initTo) return;

    setIgnoreCommon(false);

    const preset = findPresetLabelByFromTo(initFrom, initTo);
    setRangeLabel(preset);
    setFromDate(initFrom);
    setToDate(initTo);
    setAppliedFrom(initFrom);
    setAppliedTo(initTo);
  }, [From, To]);

  useEffect(() => {
    const rt = getNhiemVuKhacReturnStateFromSearch(location.search || '');
    const hasReturnState = rt.ignoreCommon || rt.rangeLabel || rt.from || rt.to;

    if (!hasReturnState) return;

    if (rt.ignoreCommon) {
      setIgnoreCommon(true);
      setRangeLabel('');
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      return;
    }

    const nextFrom = String(rt.from || '').trim();
    const nextTo = String(rt.to || '').trim();
    const preset = rt.rangeLabel || findPresetLabelByFromTo(nextFrom, nextTo);

    setIgnoreCommon(false);
    setRangeLabel(preset);
    setFromDate(nextFrom);
    setToDate(nextTo);
    setAppliedFrom(nextFrom);
    setAppliedTo(nextTo);
  }, [location.search]);

  useEffect(() => {
    if (displayRangeLabel !== RANGE_CUSTOM) return;
    if (rangeLabel) return;
    if (fromDate || toDate) return;

    if (effectiveFrom || effectiveTo) {
      setFromDate(effectiveFrom || '');
      setToDate(effectiveTo || '');
    }
  }, [displayRangeLabel, rangeLabel, fromDate, toDate, effectiveFrom, effectiveTo]);

  const handleChangeRange = (e) => {
    const v = e.target.value || '';
    setRangeLabel(v);

    if (!v) {
      setIgnoreCommon(true);
      setFromDate('');
      setToDate('');
      setAppliedFrom('');
      setAppliedTo('');
      return;
    }

    if (v !== RANGE_CUSTOM) {
      setIgnoreCommon(false);
      const { from, to } = getRangeFromTo(v);
      setFromDate(from);
      setToDate(to);
      setAppliedFrom(from);
      setAppliedTo(to);
      return;
    }

    const baseFrom = hasLocalApplied ? appliedFrom : (ignoreCommon ? '' : commonFrom);
    const baseTo = hasLocalApplied ? appliedTo : (ignoreCommon ? '' : commonTo);
    setFromDate(String(baseFrom || '').trim());
    setToDate(String(baseTo || '').trim());
  };

  const handleApplyCustomRange = () => {
    if (displayRangeLabel === RANGE_CUSTOM && !fromDate && !toDate) {
      toast.error(messageToast?.messageToastChooseDateError || 'Mời chọn ngày');
      return;
    }
    setIgnoreCommon(false);
    setAppliedFrom(fromDate || '');
    setAppliedTo(toDate || '');
  };

  const handleClearFilter = () => {
    setIgnoreCommon(true);
    setRangeLabel('');
    setFromDate('');
    setToDate('');
    setAppliedFrom('');
    setAppliedTo('');
  };

  useEffect(() => {
    return () => {
      dispatch(baoCaoClearChiTietNhiemVuKhac());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      baoCaoGetChiTietNhiemVuKhacAction({
        From: effectiveFrom,
        To: effectiveTo,
      })
    );
  }, [dispatch, effectiveFrom, effectiveTo]);

  useEffect(() => {
    if (!errorChiTietNhiemVuKhac) return;
    toast.error(getErrMsg(errorChiTietNhiemVuKhac) || 'Không thể tải báo cáo Nhiệm vụ khác.');
  }, [errorChiTietNhiemVuKhac]);

  const rows = useMemo(() => {
    if (!Array.isArray(chiTietNhiemVuKhac)) return [];
    return chiTietNhiemVuKhac.map((x) => {
      const nhanVienId = x?.nhanVienId ?? x?.NhanVienId ?? 0;
      const tenThanhVien = x?.tenThanhVien ?? x?.TenThanhVien ?? '';

      const soLuong = toNum(x?.soLuong ?? x?.SoLuong ?? 0);
      const dangThucHien = toNum(x?.dangThucHien ?? x?.DangThucHien ?? 0);
      const hoanThanh = toNum(x?.hoanThanh ?? x?.HoanThanh ?? 0);

      return { nhanVienId, tenThanhVien, soLuong, dangThucHien, hoanThanh };
    });
  }, [chiTietNhiemVuKhac]);

  useEffect(() => {
    setActivePage(1);
  }, [rows.length, pageSize, effectiveFrom, effectiveTo]);

  const onChangeShowNumber = (e) => {
    const newSize = parseInt(e.target.value, 10) || 5;
    setPageSize(newSize);
    setActivePage(1);
  };

  const pagedRows = useMemo(() => {
    const start = (activePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, activePage, pageSize]);

  const detailPath =
    URI_INTERNAL?.BAOCAO_CHI_TIET_NHIEM_VU_KHAC_THANH_VIEN_PATH ||
    `${URI_INTERNAL.BAOCAO_THONGKE_PATH}/chi-tiet-nhiem-vu-khac-thanh-vien`;

  const filterHint =
    effectiveFrom || effectiveTo
      ? `${fmtDDMMYYYY(effectiveFrom) || '...'} → ${fmtDDMMYYYY(effectiveTo) || '...'}`
      : '';

  const returnUrl = useMemo(() => {
    const sp = new URLSearchParams(location.search || '');

    if (ignoreCommon) sp.set('nvkIgnoreCommon', '1');
    else sp.delete('nvkIgnoreCommon');

    if (!ignoreCommon && rangeLabel) sp.set('nvkRangeLabel', String(rangeLabel));
    else sp.delete('nvkRangeLabel');

    if (!ignoreCommon && appliedFrom) sp.set('nvkFrom', String(appliedFrom));
    else sp.delete('nvkFrom');

    if (!ignoreCommon && appliedTo) sp.set('nvkTo', String(appliedTo));
    else sp.delete('nvkTo');

    const qs = sp.toString();
    return encodeURIComponent(`${location.pathname}${qs ? `?${qs}` : ''}`);
  }, [location.pathname, location.search, ignoreCommon, rangeLabel, appliedFrom, appliedTo]);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Báo cáo Nhiệm vụ khác</h3>
      </div>

      <div className="card-body">
        <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
          <div style={{ minWidth: 220 }}>
            <label className="form-label mb-1">Khoảng thời gian</label>
            <select
              className="form-select form-select-sm"
              value={displayRangeLabel}
              onChange={handleChangeRange}
              disabled={loadingChiTietNhiemVuKhac}
            >
              <option value="">-- Chọn --</option>
              {RANGE_OPTIONS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </div>

          {displayRangeLabel === RANGE_CUSTOM && (
            <>
              <div>
                <label className="form-label mb-1">Từ ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  disabled={loadingChiTietNhiemVuKhac}
                />
              </div>

              <div>
                <label className="form-label mb-1">Đến ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={loadingChiTietNhiemVuKhac}
                />
              </div>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleApplyCustomRange}
                disabled={loadingChiTietNhiemVuKhac}
              >
                Lọc
              </button>
            </>
          )}

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleClearFilter}
            disabled={loadingChiTietNhiemVuKhac}
          >
            Xóa lọc
          </button>

          {filterHint ? (
            <div className="filter-hint" style={{ width: '100%' }}>
              Đang lọc : {filterHint}
            </div>
          ) : null}
        </div>

        {loadingChiTietNhiemVuKhac ? (
          <div>Đang tải...</div>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <form className="form-inline">
                <label className="mr-2">Hiển thị :</label>
                <select className="form-control" value={pageSize} onChange={onChangeShowNumber}>
                  {objectShowNumber.map((item, idx) => (
                    <option key={idx} value={item.value}>
                      {item.text}
                    </option>
                  ))}
                </select>
              </form>
            </div>

            <Table
              striped
              bordered
              hover
              className="text-center align-middle w-100"
              style={{ tableLayout: 'fixed' }}
            >
              <colgroup>
                <col style={{ width: '70px' }} />
                <col style={{ width: '260px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '140px' }} />
                <col style={{ width: '110px' }} />
              </colgroup>

              <thead>
                <tr>
                  <th>STT</th>
                  <th>Thành viên</th>
                  <th>Tổng nhiệm vụ</th>
                  <th>Đang thực hiện</th>
                  <th>Hoàn thành</th>
                  <th>Chức năng</th>
                </tr>
              </thead>

              {pagedRows.length > 0 ? (
                <tbody>
                  {pagedRows.map((r, idx) => (
                    <tr key={`${r.nhanVienId}-${idx}`}>
                      <td>{(activePage - 1) * pageSize + idx + 1}</td>

                      <td className="text-center">
                        <span className="d-block w-100 text-truncate" title={r.tenThanhVien}>
                          {r.tenThanhVien}
                        </span>
                      </td>

                      <td style={{ fontWeight: 700, color: 'red' }}>{r.soLuong}</td>
                      <td>{r.dangThucHien}</td>
                      <td>{r.hoanThanh}</td>

                      <td>
                        <ul className="nav justify-content-center">
                          <li className="nav-item">
                            <NavLink
                              to={`${detailPath}?nhanVienId=${r.nhanVienId}${
                                r.tenThanhVien
                                  ? `&tenThanhVien=${encodeURIComponent(r.tenThanhVien)}`
                                  : ''
                              }${effectiveFrom ? `&From=${encodeURIComponent(effectiveFrom)}` : ''}${
                                effectiveTo ? `&To=${encodeURIComponent(effectiveTo)}` : ''
                              }&returnUrl=${returnUrl}`}
                              exact
                              className="nav-link"
                              title="Xem chi tiết"
                            >
                              <i className="nav-icon font-icon fas fa-eye" />
                            </NavLink>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td colSpan="6">Không có dữ liệu</td>
                  </tr>
                </tbody>
              )}
            </Table>

            {rows.length > pageSize && (
              <div className="dataTables_paginate paging_simple_numbers">
                <Pagination
                  itemClass="page-item"
                  linkClass="page-link"
                  innerClass="pagination pagination-sm no-margin pull-right"
                  activePage={activePage}
                  itemsCountPerPage={Number(pageSize)}
                  totalItemsCount={rows.length}
                  pageRangeDisplayed={5}
                  onChange={(p) => setActivePage(p)}
                  firstPageText="«"
                  prevPageText="‹"
                  nextPageText="›"
                  lastPageText="»"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BaoCaoChiTietNhiemVuKhac;